import { AbstractViewModel, useViewModel } from '@footsteps/shared';
import type { AttachmentSource } from '../woz-case-manager/woz-case/woz-case.types';
import type { AttachmentUploadState } from './attachment.state';
import type { AttachmentUploadFn } from './attachment.types';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
];

export class AttachmentUploadViewModel extends AbstractViewModel<AttachmentUploadState> {
  static readonly MAX_FILE_SIZE = MAX_FILE_SIZE;
  static readonly ALLOWED_TYPES = ALLOWED_TYPES;

  private readonly source: AttachmentSource;
  private readonly caseComplaintId?: string;
  private readonly uploadFn: AttachmentUploadFn;

  constructor(source: AttachmentSource, uploadFn: AttachmentUploadFn, caseComplaintId?: string) {
    super({ isDragOver: false, error: null, isUploading: false, disabled: false });
    this.source = source;
    this.caseComplaintId = caseComplaintId;
    this.uploadFn = uploadFn;

    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
  }

  get isDisabled(): boolean {
    const { disabled, isUploading } = this.state.getValue();
    return disabled || isUploading;
  }

  setUploading = (value: boolean): void => {
    this.update((state) => {
      state.isUploading = value;
    });
  };

  setDisabled = (value: boolean): void => {
    this.update((state) => {
      state.disabled = value;
    });
  };

  private validateFile(file: File): string | null {
    if (file.size > MAX_FILE_SIZE) {
      return `Bestand is te groot (max ${MAX_FILE_SIZE / 1024 / 1024} MB)`;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Bestandstype niet toegestaan';
    }
    return null;
  }

  async processFiles(files: FileList | File[]): Promise<void> {
    this.update((state) => {
      state.error = null;
    });

    for (const file of Array.from(files)) {
      const validationError = this.validateFile(file);
      if (validationError) {
        this.update((state) => {
          state.error = validationError;
        });
        return;
      }
      try {
        await this.uploadFn(
          file,
          this.source,
          this.caseComplaintId ? { caseComplaintId: this.caseComplaintId } : undefined
        );
      } catch {
        this.update((state) => {
          state.error = 'Upload mislukt. Probeer het opnieuw.';
        });
      }
    }
  }

  canUpload(): boolean {
    const { disabled, isUploading } = this.state.getValue();
    return !disabled && !isUploading;
  }

  handleDragOver(event: React.DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.update((state) => {
      state.isDragOver = true;
    });
  }

  handleDragLeave(event: React.DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.update((state) => {
      state.isDragOver = false;
    });
  }

  handleDrop(event: React.DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.update((state) => {
      state.isDragOver = false;
    });
    if (this.isDisabled) return;
    if (event.dataTransfer.files.length > 0) this.processFiles(event.dataTransfer.files);
  }

  handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.files && event.target.files.length > 0) {
      this.processFiles(event.target.files);
    }
    event.target.value = '';
  }
}

export function useAttachmentUploadViewModel(
  source: AttachmentSource,
  uploadFn: AttachmentUploadFn,
  caseComplaintId?: string
) {
  return useViewModel<AttachmentUploadState, AttachmentUploadViewModel>(
    () => new AttachmentUploadViewModel(source, uploadFn, caseComplaintId)
  );
}
